/**
 * Calendar Page
 *
 * Refactored to use modular sub-components for improved maintainability.
 * All state management is handled by the useCalendar hook.
 */

import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Loader2, Plus, ChevronLeft, ChevronRight } from 'lucide-react';
import {
  useCalendar,
  CalendarView,
  ListView,
  FilterBar,
  EventDetailModal,
  CreateEventModal,
} from './calendar-module';
import { useIsCustomer } from '../hooks/usePortalMode';

export default function Calendar() {
  const {
    // View states
    viewMode,
    calendarView,
    currentDate,
    setViewMode,
    setCalendarView,

    // Data
    events,
    suppliers,
    loading,
    error,
    calendarEvents,
    allListEvents,
    paginatedEventsByMonth,

    // Filters
    activeEventTypes,
    selectedSupplier,
    eventPeriod,
    setSelectedSupplier,
    setEventPeriod,
    handleEventTypeToggle,

    // Pagination
    currentPage,
    totalPages,
    setCurrentPage,

    // Modal states
    eventModal,
    isEditing,
    editForm,
    createModalOpen,
    setIsEditing,
    setEditForm,
    setCreateModalOpen,

    // Actions
    fetchEvents,
    handleEventClick,
    handleSaveEvent,
    handleCompleteEvent,
    handleDeleteEvent,
    handleOpenCreateModal,
    handleSelectSlot,
    handleCreateEvent,

    // Navigation
    handleNavigate,
    handleNavigateToday,
    handleNavigatePrevious,
    handleNavigateNext,
  } = useCalendar();

  const isCustomer = useIsCustomer();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">Error: {error}</p>
          <button
            onClick={fetchEvents}
            className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Calendar</h1>
          <p className="text-muted-foreground mt-1">View and manage supplier-related events</p>
        </div>
        {isCustomer && (
          <Button onClick={() => handleOpenCreateModal()}>
            <Plus className="h-4 w-4 mr-2" />
            Add Event
          </Button>
        )}
      </div>

      {/* Main Card */}
      <Card>
        <CardHeader className="pb-4">
          {/* Calendar Navigation Controls */}
          {viewMode === 'calendar' && (
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={handleNavigateToday}>
                  Today
                </Button>
                <div className="flex items-center">
                  <Button variant="ghost" size="icon" onClick={handleNavigatePrevious}>
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={handleNavigateNext}>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <h2 className="text-lg font-semibold">
                {currentDate.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}
              </h2>
              <div className="w-[120px]" /> {/* Spacer for balance */}
            </div>
          )}
          <FilterBar
            viewMode={viewMode}
            calendarView={calendarView}
            events={events}
            suppliers={suppliers}
            activeEventTypes={activeEventTypes}
            selectedSupplier={selectedSupplier}
            eventPeriod={eventPeriod}
            onViewModeChange={setViewMode}
            onCalendarViewChange={setCalendarView}
            onSupplierChange={setSelectedSupplier}
            onEventPeriodChange={setEventPeriod}
            onEventTypeToggle={handleEventTypeToggle}
          />
        </CardHeader>

        <CardContent>
          {viewMode === 'calendar' ? (
            <CalendarView
              events={calendarEvents}
              view={calendarView}
              date={currentDate}
              onEventClick={handleEventClick}
              onSelectSlot={handleSelectSlot}
              onNavigate={handleNavigate}
            />
          ) : (
            <ListView
              allListEvents={allListEvents}
              paginatedEventsByMonth={paginatedEventsByMonth}
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              onEventClick={handleEventClick}
            />
          )}
        </CardContent>
      </Card>

      {/* Event Details Modal */}
      <EventDetailModal
        open={eventModal.isOpen}
        event={eventModal.data}
        isEditing={isEditing}
        editForm={editForm}
        onClose={eventModal.close}
        onEdit={() => setIsEditing(true)}
        onCancelEdit={() => setIsEditing(false)}
        onSave={handleSaveEvent}
        onComplete={handleCompleteEvent}
        onDelete={handleDeleteEvent}
        onFormChange={setEditForm}
        readOnly={!isCustomer}
      />

      {/* Create Event Modal — only available in customer mode */}
      {isCustomer && (
        <CreateEventModal
          open={createModalOpen}
          editForm={editForm}
          onClose={() => setCreateModalOpen(false)}
          onCreate={handleCreateEvent}
          onFormChange={setEditForm}
        />
      )}
    </div>
  );
}
